import * as React from "react";
import {Fragment, useCallback, useMemo, useState} from "react";
import {AccessCodeAlphabet, Box, LogLevel, Occupancy} from "@variocube/app-api";
import {
    BoxAccess, BoxTypeSelectionResult,
    BoxUnusableDialog,
    MessageView,
    PendingView,
    PushButton,
    useAsync,
    useBoxes,
    useFlag,
    usePlatform,
    usePreventDeactivate
} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import {SizeSelectionView} from "../SizeSelectionView";
import {BackendApiCredentials, CubeCarrier, CubeCarrierState, OutboundDelivery, Sender} from "../types";
import {useCarrierId, useCarriers} from "../CarrierProvider";
import {BoxClose} from "../BoxClose";
import {useSetMessage} from '../DialogMessageProvider';
import {CustomerStoreEventHandler} from './customer-store-event-handlers';
import CustomerDataInputView from './CustomerDataInputView';
import {ReceiptData, ReceiptHandlingView} from './ReceiptHandlingView';
import {useLocalization} from "../../i18n";

interface CustomerStoreFlowProps {
    backendApiCredentials?: BackendApiCredentials;
    delivery: OutboundDelivery;
    onDone: () => void;
}

export function CustomerStore({backendApiCredentials, delivery, onDone}: CustomerStoreFlowProps) {
    const platform = usePlatform();
    const {t, language} = useLocalization();
    const boxes = useBoxes();
    const [boxSelection, setBoxSelection] = useState<BoxTypeSelectionResult>();
    const [sender, setSender] = useState<Sender>();
    const [doneOnBoxClose] = useState<boolean>();
    const [showReceiptView, setShowReceiptView] = useState<boolean>();
    const carriers = useCarriers();
    const carrierId = useCarrierId();
    const [loadingEventHandler, setLoadingEventHandler] = useState<boolean>();
    const [senderSubmitted, setSenderSubmitted] = useState(!delivery.needSender);
    const [boxUnusable, setBoxUnusable] = useState(false);
    const [preExistingOccupancy, setPreExistingOccupancy] = useState<Occupancy>();
    const [canceled, setCanceled] = useFlag(false);
    const [closeBox, setCloseBox] = useState<Box>();
    const [switchingBoxFailed, setSwitchingBoxFailed] = useState(false);
    const setMessage = useSetMessage();
    const customerStoreEventHandler = useMemo(() => new CustomerStoreEventHandler(), []);
    const cubeCarrier = useMemo<CubeCarrier | undefined>(() => {
        if (carriers && delivery.carrier) {
            return carriers[delivery.carrier];
        }
    }, [carriers, delivery]);

    const firstMileEnabled = useMemo<boolean>(() => {
        if(carriers && carrierId) {
            const carrier = carriers[carrierId];
            if(carrier && carrier.state) {
                switch (carrier.state) {
                    case CubeCarrierState.Disabled:
                    case CubeCarrierState.LastMileOnly:
                        return false;
                }
            }
        }
        return true;
    }, [carriers, carrierId]);

    useAsync(async () => {
        if (!firstMileEnabled) return;
        if (carrierId === "ups") {
            // for ups there is no way to distinguish between first-mile and last-mile packages
            // if there is a reservation, it will be automatically taken as last-mile, if not
            // the driver has to log in with the driver code at first
            // if the driver forgets to log in, the package will be taken as first-mile, as discussed with ups
            // due to a high rate of last-mile packages that were taken as first-mile due to driver error, we added this dialog
            setMessage({
                title: t("UpsFirstMilePackageTitle"),
                message: t("UpsFirstMilePackageDescription"),
                confirm: () => {
                    setMessage(undefined);
                    handleInitCustomerStore();
                },
                cancel: () => {
                    setMessage(undefined);
                    onDone();
                },
                disableAutoClose: true,
            });
        } else {
            await handleInitCustomerStore();
        }
    }, []);

    const handleInitCustomerStore = useCallback(async () => {
        if (!firstMileEnabled) return;
        const foundPreExistingOccupancy = platform.occupancies.filter(occupancy => occupancy.content.barCode === delivery.barcode)?.[0];

        if (foundPreExistingOccupancy) {
            setPreExistingOccupancy(foundPreExistingOccupancy);
            return;
        }

        setLoadingEventHandler(true);
        try {
            await customerStoreEventHandler.parcelScanned({
                carrierId: delivery.carrier,
                parcelId: delivery.barcode,
                cubeId: platform.hostname,
                backendApiCredentials: backendApiCredentials!,
                strings: t,
                setMessage,
                cancelToHomeScreen: () => {
                    handleCancel();
                    setMessage(undefined);
                    onDone();
                },
            });
        } catch (err) {
            console.error("Error confirming occupancy", err);
            setMessage({
                title: t("ParcelNotAcceptedTitle"),
                message: t("ParcelNotAcceptedDescription", {barCode: delivery.barcode}),
            });
            onDone();
        } finally {
            setLoadingEventHandler(false);
        }
    }, []);

    const {error, loading, result: occupancy} = useAsync(async () => {
        if (preExistingOccupancy || !boxSelection || !firstMileEnabled) {
            return undefined;
        }

        return await platform.occupy({
            ...boxSelection,
            accessCodeShape: {
                alphabet: AccessCodeAlphabet.OneToNine,
                length: 7,
            },
            content: {
                barCode: delivery.barcode,
                carrierId: delivery.carrier,
                storedAt: DateTime.utc().toISO(),
                sender,
            },
        });
    }, [platform, boxSelection, delivery, sender]);

    const box = useMemo(() => boxes.find(box => box.number == occupancy?.boxNumber), [boxes, occupancy]);

    const handleBoxAccessConfirm = useCallback(async () => {
        if (occupancy) {
            try {
                await platform.confirmOccupancy(occupancy.uuid);
                await platform.log(LogLevel.Audit, "Customer Store", {delivery})
                setMessage({
                    title: t("ParcelStoreSuccessTitle"),
                    message: t("ParcelStoreSuccessDescription"),
                });
            } catch (error) {
                console.error("Error confirming occupancy", occupancy);
            }
        }
        if (cubeCarrier?.firstMile?.offerReceipt) {
            setShowReceiptView(true);
        } else {
            onDone();
        }
    }, [platform, occupancy, delivery, onDone, cubeCarrier, sender, box]);

    const handleCancel = useCallback(async () => {
        setLoadingEventHandler(true);
        try {
            await customerStoreEventHandler.parcelStoreCancelled({
                carrierId: delivery.carrier,
                parcelId: delivery.barcode,
                cubeId: platform.hostname,
                backendApiCredentials: backendApiCredentials!,
            });
        } catch (err) {
            console.error("Error cancelling parcel store", occupancy);
        } finally {
            setLoadingEventHandler(false);
            onDone();
        }
    }, [delivery, cubeCarrier, customerStoreEventHandler, onDone]);

    const handleSenderSubmit = useCallback((sender: Sender) => {
        setSender(sender);
        setSenderSubmitted(true);
    }, []);

    const handleReceiptHandlingCancel = useCallback(() => {
        onDone();
    }, [onDone]);

    const handleReceiptHandlingConfirm = useCallback(async (data: ReceiptData) => {
        setLoadingEventHandler(true);
        try {
            await customerStoreEventHandler.sendReceiptConfirmed({
                carrierId: delivery.carrier,
                parcelId: delivery.barcode,
                cubeId: platform.hostname,
                backendApiCredentials: backendApiCredentials!,
                email: data.email,
                phone: data.phone,
                language: language,
                setMessage,
            });
        } catch (err) {
            console.error("Error sending receipt", occupancy);
        } finally {
            setLoadingEventHandler(false);
            onDone();
        }
    }, [onDone, delivery, cubeCarrier, setMessage, customerStoreEventHandler, onDone]);

    const handleSizeSelectionBack = useCallback(() => {
        if (cubeCarrier?.firstMile?.availableFields?.[0]) {
            setSenderSubmitted(false);
        } else {
            onDone();
        }
    }, [cubeCarrier]);

    const handleOtherSize = useCallback(async () => {
        setBoxUnusable(false);
        if (occupancy) {
            await platform.cancelOccupancy(occupancy.uuid);
        }
        setBoxSelection(undefined);
        setCloseBox(box);
    }, [occupancy, platform, box]);

    const handleBoxNotEmpty = useCallback(async () => {
        try {
            if(!occupancy)
                return;

            const newContent = {
                carrierId: "unknown",
                deliveryId: "unknown",
                reactivateLastOccupancy: true,
            };
            await platform.updateOccupancy(occupancy.uuid, newContent);
            await platform.confirmOccupancy(occupancy.uuid);

            setBoxSelection(undefined)
            setCloseBox(box);
        } catch (error) {
            console.error("Error switching occupancies", error);
            setSwitchingBoxFailed(true);
        } finally {
            setBoxUnusable(false);
        }
    }, [platform, occupancy, box]);

    const handleBoxUnusable = useCallback(() => setBoxUnusable(true), []);
    const handleBoxUnusableClose = useCallback(() => setBoxUnusable(false), []);

    const handleBoxCloseDone = useCallback(() => {
        setCloseBox(undefined);
        if (doneOnBoxClose) {
            onDone();
        }
    }, [doneOnBoxClose]);

    usePreventDeactivate();

    if(!firstMileEnabled) {
        return (
            <MessageView message={t("FirstMileDisabled", {carrierName: carrierId ? carrierId.toUpperCase() : ''})}>
                <PushButton onClick={() => handleCancel()}>
                    {t("Close")}
                </PushButton>
            </MessageView>
        );
    }

    if (closeBox) {
        return (
            <BoxClose box={closeBox} onDone={handleBoxCloseDone}/>
        )
    }
    if (switchingBoxFailed) {
        return (
            <MessageView message={t("SwitchingBoxFailed")}>
                <PushButton onClick={() => setSwitchingBoxFailed(false)}>
                    {t("Close")}
                </PushButton>
            </MessageView>
        );
    } else if (loading || loadingEventHandler) {
        return <PendingView message={t("ProcessingParcelStore")}/>;
    } else if (preExistingOccupancy) {
        return (
            <MessageView message={t("AlreadyStored")}>
                <PushButton onClick={handleCancel}>
                    {t("Cancel")}
                </PushButton>
            </MessageView>
        );
    } else if (!senderSubmitted && cubeCarrier?.firstMile?.availableFields?.[0]) {
        return (
            <CustomerDataInputView
                sender={sender}
                carrierId={delivery.carrier}
                onBack={handleCancel}
                onSubmit={handleSenderSubmit}
            />
        );
    } else if (!boxSelection) {
        return (
            <SizeSelectionView
                onBack={handleSizeSelectionBack}
                onSelect={setBoxSelection}
            />
        );
    } else if (loading) {
        return <PendingView message={t("OccupyPending")}/>;
    } else if (error) {
        return (
            <MessageView message={t("BoxTypeNotAvailable")}>
                <PushButton onClick={handleOtherSize}>
                    {t("SelectOtherSize")}
                </PushButton>
                <PushButton onClick={handleCancel}>
                    {t("Cancel")}
                </PushButton>
            </MessageView>
        );
    } else if (canceled && box) {
        return (
            <BoxClose box={box} onDone={handleCancel}/>
        )
    } else if (occupancy && box && !showReceiptView) {
        return (
            <Fragment>
                <BoxAccess
                    boxes={box}
                    platform={platform}
                    onCancel={setCanceled}
                    disableConfirm={cubeCarrier?.firstMile?.reopenCountdown ? cubeCarrier?.firstMile?.reopenCountdown <= 1 : false}
                    onConfirm={handleBoxAccessConfirm}
                    onBoxUnusable={handleBoxUnusable}
                    stopAutoConfirm={boxUnusable}
                    boxOpenTitle={t("StoreTitle")}
                    boxOpenMessage={t("StoreMessage")}
                    confirmTitle={t("ConfirmStoreTitle")}
                    confirmMessage={t("ConfirmStoreMessage")}
                    strings={t}
                    autoConfirm={cubeCarrier?.firstMile.reopenCountdown}
                />
                <BoxUnusableDialog
                    open={boxUnusable}
                    onClose={handleBoxUnusableClose}
                    onWrongSize={handleOtherSize}
                    // onNotEmpty={handleBoxNotEmpty}
                    onCancel={setCanceled}
                    strings={t}
                />
            </Fragment>
        );
    } else if (showReceiptView) {
        return <ReceiptHandlingView onConfirm={handleReceiptHandlingConfirm} onCancel={handleReceiptHandlingCancel}/>
    }
    return null;
}